import { useState, useEffect } from "react";
import QuestionAnalysis from "./QuestionAnalysis";
import { reportsAPI } from "../../services/api";
import {
  Box, Typography, Card, CardContent, Grid, Chip,
  CircularProgress, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, LinearProgress, Button
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const StatCard = ({ title, value, subtitle, color = "#1a237e" }) => (
  <Card sx={{ borderRadius: 3, boxShadow: 2, height: "100%" }}>
    <CardContent sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" mb={1}>{title}</Typography>
      <Typography variant="h3" fontWeight={700} color={color}>{value}</Typography>
      {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
    </CardContent>
  </Card>
);

export default function AnalyticsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAllQuestions, setShowAllQuestions] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await reportsAPI.getAnalyticsDashboard();
      setData(res.data);
    } catch { setError("Failed to load analytics."); }
    finally { setLoading(false); }
  };

  const exportPDF = () => {
    if (!data) return;
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.setTextColor(26, 35, 126);
    doc.text("Synaptix - Analytics Report", 14, 20);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 28);

    // Summary
    doc.setFontSize(13);
    doc.setTextColor(26, 35, 126);
    doc.text("Summary", 14, 40);
    autoTable(doc, {
      startY: 45,
      head: [["Metric", "Value"]],
      body: [
        ["Total Agents", data.summary.total_agents],
        ["Total Enrolments", data.summary.total_enrolments],
        ["Completion Rate", data.summary.completion_rate + "%"],
        ["Total Attempts", data.summary.total_attempts],
        ["Pass Rate", data.summary.pass_rate + "%"],
        ["Passed", data.summary.passed_attempts],
        ["Failed", data.summary.failed_attempts],
      ],
      headStyles: { fillColor: [26, 35, 126] },
      styles: { fontSize: 9 }
    });

    // Score Distribution
    doc.setFontSize(13);
    doc.setTextColor(26, 35, 126);
    doc.text("Score Distribution", 14, doc.lastAutoTable.finalY + 15);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 20,
      head: [["Score Range", "Count"]],
      body: Object.entries(data.score_distribution).map(([k, v]) => [k + "%", v]),
      headStyles: { fillColor: [26, 35, 126] },
      styles: { fontSize: 9 }
    });

    // Team Performance
    if (data.team_performance.length > 0) {
      doc.setFontSize(13);
      doc.setTextColor(26, 35, 126);
      doc.text("Team Performance", 14, doc.lastAutoTable.finalY + 15);
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 20,
        head: [["Team", "Agents", "Attempts", "Pass Rate", "Avg Score", "Certified"]],
        body: data.team_performance.map(t => [
          t.team_name, t.agent_count, t.total_attempts,
          t.pass_rate + "%", t.avg_score ? t.avg_score + "%" : "-", t.certified
        ]),
        headStyles: { fillColor: [26, 35, 126] },
        styles: { fontSize: 9 }
      });
    }

    // Top Failed Questions
    doc.addPage();
    doc.setFontSize(13);
    doc.setTextColor(26, 35, 126);
    doc.text("Top Failed Questions", 14, 20);
    autoTable(doc, {
      startY: 25,
      head: [["Question", "Module", "Times Used", "Failed", "Fail Rate"]],
      body: data.top_failed_questions.map(q => [
        q.question_text.substring(0, 60), q.module_title,
        q.times_used, q.times_failed, q.fail_rate + "%"
      ]),
      headStyles: { fillColor: [26, 35, 126] },
      styles: { fontSize: 8 }
    });

    // Avg Time
    if (data.avg_time_per_assessment.length > 0) {
      doc.setFontSize(13);
      doc.setTextColor(26, 35, 126);
      doc.text("Average Time per Assessment", 14, doc.lastAutoTable.finalY + 15);
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 20,
        head: [["Assessment", "Avg Time (min)", "Attempts"]],
        body: data.avg_time_per_assessment.map(t => [t.template, t.avg_minutes, t.attempts]),
        headStyles: { fillColor: [26, 35, 126] },
        styles: { fontSize: 9 }
      });
    }

    doc.save("synaptix_analytics_report.pdf");
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const { summary, score_distribution, avg_time_per_assessment, top_failed_questions, team_performance } = data;

  return (
    <Box>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1a237e">Analytics Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">Real-time training performance overview</Typography>
        </Box>
        <Button variant="outlined" color="error" startIcon={<DownloadIcon />} onClick={exportPDF}>
          Export PDF Report
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={2}>
          <StatCard title="Total Agents" value={summary.total_agents} />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard title="Completion Rate" value={summary.completion_rate + "%"}
            color={summary.completion_rate >= 70 ? "#2e7d32" : "#c62828"} />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard title="Total Attempts" value={summary.total_attempts} />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard title="Pass Rate" value={summary.pass_rate + "%"}
            color={summary.pass_rate >= 70 ? "#2e7d32" : "#c62828"} />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard title="Passed" value={summary.passed_attempts} color="#2e7d32" />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard title="Failed" value={summary.failed_attempts} color="#c62828" />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Score Distribution */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} mb={2} color="#1a237e">Score Distribution</Typography>
              {Object.entries(score_distribution).map(([range, count]) => (
                <Box key={range} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="body2">{range}%</Typography>
                    <Typography variant="body2" fontWeight={600}>{count} attempts</Typography>
                  </Box>
                  <LinearProgress variant="determinate"
                    value={summary.total_attempts > 0 ? (count / summary.total_attempts) * 100 : 0}
                    sx={{ height: 10, borderRadius: 5,
                      "& .MuiLinearProgress-bar": {
                        bgcolor: range === "90-100" ? "#2e7d32" : range === "80-89" ? "#388e3c" :
                                 range === "70-79" ? "#f57c00" : range === "60-69" ? "#ef6c00" : "#c62828"
                      }
                    }} />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Avg Time per Assessment */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} mb={2} color="#1a237e">Avg Time per Assessment</Typography>
              {avg_time_per_assessment.length === 0 ? (
                <Typography color="text.secondary">No time data available yet.</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f5f7fa" }}>
                        <TableCell><b>Assessment</b></TableCell>
                        <TableCell><b>Avg Time</b></TableCell>
                        <TableCell><b>Attempts</b></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {avg_time_per_assessment.map((t, i) => (
                        <TableRow key={i} hover>
                          <TableCell><Typography variant="body2">{t.template}</Typography></TableCell>
                          <TableCell><Chip label={`${t.avg_minutes} min`} size="small" color="primary" /></TableCell>
                          <TableCell>{t.attempts}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Team Performance */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} mb={2} color="#1a237e">Team Performance Comparison</Typography>
              {team_performance.length === 0 ? (
                <Typography color="text.secondary">No team data available.</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f5f7fa" }}>
                        <TableCell><b>Team</b></TableCell>
                        <TableCell><b>Agents</b></TableCell>
                        <TableCell><b>Attempts</b></TableCell>
                        <TableCell><b>Pass Rate</b></TableCell>
                        <TableCell><b>Avg Score</b></TableCell>
                        <TableCell><b>Certified</b></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {team_performance.map((t, i) => (
                        <TableRow key={i} hover>
                          <TableCell><Typography variant="body2" fontWeight={600}>{t.team_name}</Typography></TableCell>
                          <TableCell>{t.agent_count}</TableCell>
                          <TableCell>{t.total_attempts}</TableCell>
                          <TableCell>
                            <Chip label={t.pass_rate + "%"} size="small"
                              color={t.pass_rate >= 80 ? "success" : t.pass_rate >= 60 ? "warning" : "error"} />
                          </TableCell>
                          <TableCell>{t.avg_score ? t.avg_score + "%" : "-"}</TableCell>
                          <TableCell>
                            <Chip label={t.certified} size="small" color={t.certified > 0 ? "success" : "default"} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Top Failed Questions */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} mb={2} color="#1a237e">Top 10 Most Failed Questions</Typography>
              {top_failed_questions.length === 0 ? (
                <Typography color="text.secondary">No question attempt data yet.</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f5f7fa" }}>
                        <TableCell><b>Question</b></TableCell>
                        <TableCell><b>Module</b></TableCell>
                        <TableCell><b>Difficulty</b></TableCell>
                        <TableCell><b>Times Used</b></TableCell>
                        <TableCell><b>Failed</b></TableCell>
                        <TableCell><b>Fail Rate</b></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {top_failed_questions.map((q, i) => (
                        <TableRow key={i} hover>
                          <TableCell sx={{ maxWidth: 350 }}>
                            <Typography variant="body2" fontWeight={q.is_critical ? 700 : 400}>
                              {q.question_text}
                            </Typography>
                            {q.is_critical && <Chip label="Critical" size="small" color="error" sx={{ mt: 0.5 }} />}
                          </TableCell>
                          <TableCell><Typography variant="body2">{q.module_title}</Typography></TableCell>
                          <TableCell>
                            <Chip label={q.difficulty} size="small"
                              color={q.difficulty === "hard" ? "error" : q.difficulty === "medium" ? "warning" : "success"} />
                          </TableCell>
                          <TableCell>{q.times_used}</TableCell>
                          <TableCell sx={{ color: "error.main", fontWeight: 600 }}>{q.times_failed}</TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <LinearProgress variant="determinate" value={q.fail_rate}
                                sx={{ width: 60, height: 8, borderRadius: 4,
                                  "& .MuiLinearProgress-bar": {
                                    bgcolor: q.fail_rate > 50 ? "#d32f2f" : q.fail_rate > 25 ? "#f57c00" : "#388e3c"
                                  }
                                }} />
                              <Typography variant="body2" fontWeight={600}
                                color={q.fail_rate > 50 ? "error.main" : q.fail_rate > 25 ? "warning.main" : "success.main"}>
                                {q.fail_rate}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              <Box sx={{ mt: 2, textAlign: "right" }}>
                <Button variant="text" onClick={() => setShowAllQuestions(!showAllQuestions)}>
                  {showAllQuestions ? "Hide Full Analysis" : "View All Questions →"}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {showAllQuestions && (
          <Grid item xs={12}>
            <QuestionAnalysis />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
